// src/api/mentions.api.ts — SCE Issues
// Mentionner quelqu'un avec « @ » (02/11) : les personnes choisies dans le
// menu de la zone de saisie (components/mentions/ZoneMentions), puis la
// notification déposée dans SCE_Envois, que le flow 6 envoie. Règles et
// message : utils/mentions.
//
// Les personnes choisies sont gardées pour l'onglet : au moment d'enregistrer,
// le texte dit qui est encore mentionné (une mention effacée ne prévient plus).
//
// Pendant l'essai (config/mentions) : le message part au seul administrateur,
// même quand tout le monde est écarté, avec qui l'aurait reçu.
//
// Toujours au mieux : le commentaire ou la déclaration est déjà écrit ; un
// échec ici est noté dans la console et dit en fin de message, jamais bloquant.

import type { Issue } from '@/types/issue.types'
import { piloteLeProbleme } from '@/config/sujets.config'
import { DESTINATAIRE_ESSAI_MENTIONS, MENTIONS_EN_ESSAI } from '@/config/mentions.config'
import { deposerLigneEnvoi } from './envoisBE.api'
import { deposerNotificationsAppli } from './cloche.api'
import { lienProbleme } from '@/utils/liensFiches'
import { acteurNotifications, urlAppNotifications } from './notificationsFnct.api'
import { lireReglagesMentionne } from './userPrefs.api'
import { resolveUserLookupId } from './users.api'
import {
  champsMention,
  destinatairesMention,
  mentionsDans,
  messageMention,
  nouvellesMentions,
  REGLAGES_INCONNUS,
  type GesteMention,
  type PersonneMentionnee,
  type ReglagesMentionne,
} from '@/utils/mentions'

const choisies = new Map<string, PersonneMentionnee>()

/** Une personne choisie dans le menu « @ » de cet onglet. */
export function retenirMention(p: PersonneMentionnee): void {
  const cle = p.upn.trim().toLowerCase()
  if (cle) choisies.set(cle, { nom: p.nom.trim(), upn: cle })
}

export interface DemandeMention {
  issue: Pick<Issue, 'id' | 'titre' | 'metier' | 'sujet' | 'statut' | 'numeroSerie' | 'pn' | 'abonnes'>
  geste: GesteMention
  /** Le texte enregistré. */
  texte: string
  /** Une correction : le texte d'avant (seules les mentions ajoutées préviennent). */
  avant?: string
  /** Les adresses de l'auteur, pour ne pas se prévenir soi-même. */
  auteur: readonly string[]
  modele?: string
}

export interface BilanMentions {
  prevenus: PersonneMentionnee[]
  echec: boolean
  /** Message d'essai, parti à l'administrateur seul. */
  essai?: boolean
}

const RIEN: BilanMentions = { prevenus: [], echec: false }

/** Les personnes mentionnées dans ce texte (choisies dans cet onglet). */
export function personnesMentionnees(texte: string, avant?: string): PersonneMentionnee[] {
  const connues = [...choisies.values()]
  return avant === undefined ? mentionsDans(texte, connues) : nouvellesMentions(avant, texte, connues)
}

/** Prévient les personnes mentionnées qui ne reçoivent rien d'autre pour ce geste. */
export async function notifierMentions(d: DemandeMention): Promise<BilanMentions> {
  const mentionnes = personnesMentionnees(d.texte, d.avant)
  const qui = acteurNotifications()
  if (mentionnes.length === 0 || !qui) return RIEN
  try {
    const reglages = new Map<string, ReglagesMentionne>()
    const ids = new Map<string, number>()
    // Un abonné sans adresse lisible se reconnaît à son identifiant SharePoint.
    const sansAdresse = d.issue.abonnes.some((a) => !a.email)
    await Promise.all(
      mentionnes.map(async (p) => {
        const cle = p.upn.toLowerCase()
        reglages.set(cle, await lireReglagesMentionne(cle).catch(() => REGLAGES_INCONNUS))
        if (sansAdresse) {
          const id = await resolveUserLookupId(cle)
          if (id !== null) ids.set(cle, id)
        }
      }),
    )
    const destinataires = destinatairesMention(mentionnes, {
      geste: d.geste,
      auteur: [...d.auteur, qui.upn],
      issue: d.issue,
      pilote: (upn) => piloteLeProbleme(upn, d.issue),
      idsSharePoint: ids,
      reglages,
    })
    const { prevenus } = destinataires
    const essai = MENTIONS_EN_ESSAI
    // La cloche (03/11) : les prévenus, plus qui a réglé « l'appli seulement »
    // (pas de message pour eux, la cloche est leur canal).
    const appliSeule = destinataires.ecartes.filter((e) => e.raison === 'aucun').map((e) => e.personne)
    const dansLaCloche = essai ? [] : [...prevenus, ...appliSeule]
    await deposerNotificationsAppli(
      dansLaCloche.map((p) => ({
        destinataire: p.upn,
        reference: d.issue.id,
        genre: 'mention' as const,
        auteur: qui.nom,
        auteurUpn: qui.upn,
        quoi: 'vous a mentionné dans',
        libelle: d.issue.titre,
        extrait: d.texte,
        lien: lienProbleme(d.issue.id),
      })),
    )
    if (prevenus.length === 0 && !essai) return { prevenus: appliSeule, echec: false }
    const message = messageMention({
      issue: d.issue,
      ...(d.modele ? { modele: d.modele } : {}),
      auteur: qui.nom,
      geste: d.geste,
      texte: d.texte,
      prevenus: essai ? mentionnes : prevenus,
      ...(essai ? { essai: destinataires } : {}),
      lien: `${urlAppNotifications()}/issues/${encodeURIComponent(d.issue.id)}`,
    })
    const a = essai ? [DESTINATAIRE_ESSAI_MENTIONS] : prevenus.map((p) => p.upn)
    await deposerLigneEnvoi(champsMention(d.issue.id, message, a, qui.upn))
    return { prevenus: [...prevenus, ...appliSeule], echec: false, essai }
  } catch (err) {
    console.warn('[mentions] notification non déposée :', err)
    return { prevenus: [], echec: true }
  }
}
